import { useCallback, useEffect, useState } from 'react';
import { api } from '../../../services/api';

export interface PassageiroGrupo {
  nome: string;
  docIdentificacao: string;
  orgaoExpedidor: string;
}

export interface GrupoPassageiros {
  idGrupo: number;
  idUsuario: number;
  nome: string;
  passageiros: PassageiroGrupo[];
  dtAtualizacao: string | null;
}

/**
 * Os grupos de passageiros do cooperado.
 *
 * São os mesmos que o aplicativo grava e lê — o painel só passou a alcançá-los.
 */
export function useGruposPassageiros() {
  const [grupos, setGrupos] = useState<GrupoPassageiros[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const { data } = await api.get<GrupoPassageiros[]>('/cooperado/grupos-passageiros');
      setGrupos(data);
    } catch {
      setErro('Não foi possível carregar seus grupos.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const criar = useCallback(async (nome: string, passageiros: PassageiroGrupo[]) => {
    const { data } = await api.post<GrupoPassageiros>('/cooperado/grupos-passageiros', { nome, passageiros });
    await carregar();
    return data;
  }, [carregar]);

  const atualizar = useCallback(async (idGrupo: number, nome: string, passageiros: PassageiroGrupo[]) => {
    const { data } = await api.put<GrupoPassageiros>(`/cooperado/grupos-passageiros/${idGrupo}`, { nome, passageiros });
    await carregar();
    return data;
  }, [carregar]);

  const excluir = useCallback(async (idGrupo: number) => {
    await api.delete(`/cooperado/grupos-passageiros/${idGrupo}`);
    await carregar();
  }, [carregar]);

  return { grupos, carregando, erro, recarregar: carregar, criar, atualizar, excluir };
}

/**
 * Anota qual grupo foi usado numa ATF.
 *
 * **Nunca deixa a falha subir**: quando isto roda, a ATF já existe. Uma
 * anotação perdida é muito menos grave do que fazer o cooperado achar que a
 * emissão falhou — é a mesma escolha que o app faz.
 */
export async function vincularGrupoNaAtf(idAtfLog: number, idGrupo: number): Promise<void> {
  try {
    await api.post(`/cooperado/atf/${idAtfLog}/grupo`, { idGrupo });
  } catch {
    /* silêncio deliberado */
  }
}
